import { FlightSearchFormValidationMessages, FlightSearchParams, ItineraryPackage, PassengerInfo } from '~/types';

export type PassengerValidationMessages = Record<string, Partial<Record<keyof PassengerInfo, string[]>>>;

export type PackageCapacityValidationMessage = string | null;

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getTotalPassengers = (params: FlightSearchParams) => (params.passengers.adults || 0) + (params.passengers.children || 0) + (params.passengers.infants || 0);

const normalizeDate = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const addDays = (date: Date, days: number) => {
  const result = normalizeDate(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addMonths = (date: Date, months: number) => {
  const result = normalizeDate(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

export const getSearchValidationMessages = (fields: FlightSearchParams, dateConstraints?: ItineraryPackage['dateConstraints']): FlightSearchFormValidationMessages => {
  const validationMessages: FlightSearchFormValidationMessages = { to: [], from: [], departure: [], return: [] };
  if (!fields.from) validationMessages.from.push('* Please select where to travel from.');
  if (!fields.to) validationMessages.to.push('* Please select where to travel to.');
  if (fields.from && fields.to && fields.from.iata === fields.to.iata) validationMessages.to.push("* Origin and destination can't be the same.");
  if (!fields.departureDate) validationMessages.departure.push('* Please select departure date.');
  if (fields.type === 'return' && !fields.returnDate) validationMessages.return.push('* Please select return date.');
  if (fields.departureDate && dateConstraints) {
    const today = normalizeDate(new Date());
    const departureDate = normalizeDate(fields.departureDate);
    const minDepartureDate = addDays(today, dateConstraints.departure.minDaysFromToday);
    const maxDepartureDate = addMonths(today, dateConstraints.departure.maxMonthsFromToday);
    if (departureDate < minDepartureDate) validationMessages.departure.push(dateConstraints.departure.minDaysFromToday === 0
      ? '* Departure date cannot be before today.'
      : `* Departure must be at least ${dateConstraints.departure.minDaysFromToday} days from today.`);
    if (departureDate > maxDepartureDate) validationMessages.departure.push(`* Departure must be within ${dateConstraints.departure.maxMonthsFromToday} months from today.`);
  }
  if (fields.returnDate && fields.departureDate) {
    const returnDate = normalizeDate(fields.returnDate);
    const minReturnDate = addDays(fields.departureDate, dateConstraints?.return.minDaysAfterDeparture ?? 0);
    if (returnDate < minReturnDate) validationMessages.return.push(dateConstraints?.return.minDaysAfterDeparture ? '* Return date must be after departure date.' : "* Return date can't be before departure date.");
    if (dateConstraints && returnDate > addMonths(new Date(), dateConstraints.return.maxMonthsFromToday)) validationMessages.return.push(`* Return date must be within ${dateConstraints.return.maxMonthsFromToday} months from today.`);
  }
  return validationMessages;
};

export const getPackageCapacityValidationMessage = (params: FlightSearchParams, selectedPackage?: Pick<ItineraryPackage, 'max_travelers' | 'title'> | null): PackageCapacityValidationMessage => {
  if (!selectedPackage || typeof selectedPackage.max_travelers !== 'number') return null;

  const totalPassengers = getTotalPassengers(params);
  if (totalPassengers <= selectedPackage.max_travelers) return null;

  return `This package supports up to ${selectedPackage.max_travelers} passenger${selectedPackage.max_travelers === 1 ? '' : 's'}, but you selected ${totalPassengers}. Please change package or reduce passengers before entering customer details.`;
};

export const getPassengerDobBounds = (passengerType: PassengerInfo['passengerType']) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yearsAgo = (years: number) => {
    const date = new Date(today);
    date.setFullYear(date.getFullYear() - years);
    return date;
  };

  if (passengerType === 'Adult') {
    return { minDate: null, maxDate: yearsAgo(12), helperText: 'Adults must be 12 years or older.' };
  }

  if (passengerType === 'Child') {
    const minDate = yearsAgo(12);
    minDate.setDate(minDate.getDate() + 1);
    return { minDate, maxDate: yearsAgo(2), helperText: 'Children must be between 2 and 11 years old.' };
  }

  const minDate = yearsAgo(2);
  minDate.setDate(minDate.getDate() + 1);
  return { minDate, maxDate: today, helperText: 'Infants must be under 2 years old.' };
};

export const isDateWithinBounds = (date: Date, minDate: Date | null, maxDate: Date | null) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return (!minDate || normalized >= minDate) && (!maxDate || normalized <= maxDate);
};

export const getPassengerValidationMessages = (passengers: PassengerInfo[]): PassengerValidationMessages => passengers.reduce<PassengerValidationMessages>((messages, passenger) => {
  const passengerMessages: Partial<Record<keyof PassengerInfo, string[]>> = {};
  if (!passenger.gender) passengerMessages.gender = ['* Please select passenger gender.'];
  if (!passenger.firstName.trim()) passengerMessages.firstName = ['* Please enter first name.'];
  if (!passenger.lastName.trim()) passengerMessages.lastName = ['* Please enter surname.'];
  if (!passenger.dateOfBirth) {
    passengerMessages.dateOfBirth = ['* Please select date of birth.'];
  } else {
    const { minDate, maxDate, helperText } = getPassengerDobBounds(passenger.passengerType);
    if (!isDateWithinBounds(passenger.dateOfBirth, minDate, maxDate)) passengerMessages.dateOfBirth = [`* ${helperText}`];
  }

  if (Object.keys(passengerMessages).length) messages[passenger.id] = passengerMessages;
  return messages;
}, {});

export const hasPassengerValidationErrors = (messages: PassengerValidationMessages) => Object.keys(messages).length > 0;

export const getCustomerEmailValidationMessages = (value: string) => {
  if (!value.trim()) return ['* Please enter customer email.'];
  if (!EMAIL_REGEX.test(value.trim())) return ['* Please enter a valid customer email.'];
  return [];
};